/*
 * Pludo — Subscription Widget
 *
 * Custom element backing snippets/pludo-subscription-widget.liquid. Handles the
 * subscribe / one-time toggle, optional frequency selector, hidden `selling_plan`
 * input, and live syncing of the main + sticky price blocks.
 *
 * Price/plan/variant logic is ported from assets/subscription-widget.js so cart
 * and price behaviour stays identical to the native theme widget.
 */
if (!customElements.get("pludo-subscription-widget")) {
  customElements.define(
    "pludo-subscription-widget",
    class PludoSubscriptionWidget extends HTMLElement {
      connectedCallback() {
        this.container = this.querySelector(".pludo-sub__container");
        this.init();
        this.subscribeVariantChange();
      }

      disconnectedCallback() {
        if (typeof this.unsubscribeVariantChange === "function") {
          this.unsubscribeVariantChange();
        }
      }

      init() {
        this.sellingPlanInput = this.querySelector('input[name="selling_plan"]');
        this.options = this.querySelectorAll(".pludo-sub__option");
        this.freqSelect = this.querySelector(".pludo-sub__freq");
        this.perDaySuffix = this.dataset.perDaySuffix || "";

        this.addEventListeners();
        this.setInitialPlan();
      }

      addEventListeners() {
        this.options.forEach((opt) => {
          // <label> already toggles its radio; react to the radio change.
          const input = opt.querySelector(".pludo-sub__input");
          if (input) {
            input.addEventListener("change", () => this.selectOption(opt));
          }
        });

        if (this.freqSelect) {
          this.freqSelect.addEventListener("change", (e) => {
            e.stopPropagation();
            this.handleFreqChange();
          });
        }
      }

      setInitialPlan() {
        const selected =
          [...this.options].find((o) => o.classList.contains("is-selected")) ||
          this.options[0];
        if (selected) this.selectOption(selected);
      }

      selectOption(opt) {
        this.options.forEach((o) => {
          o.classList.remove("is-selected");
          const input = o.querySelector(".pludo-sub__input");
          if (input) input.checked = false;
        });
        opt.classList.add("is-selected");
        const input = opt.querySelector(".pludo-sub__input");
        if (input) input.checked = true;

        const isSubscription = opt.dataset.option === "subscription";
        let dataEl = opt;
        let planId = "";

        if (isSubscription) {
          if (this.freqSelect) {
            dataEl = this.freqSelect.options[this.freqSelect.selectedIndex] || opt;
            planId = dataEl.value || "";
            this.updateOptionDisplay(opt, dataEl);
          } else {
            planId = opt.dataset.sellingPlan || "";
          }
        }

        if (this.sellingPlanInput) this.sellingPlanInput.value = isSubscription ? planId : "";
        this.applyPrice(dataEl);
        this.updateURLParamsIfEnabled(isSubscription ? planId : "");
      }

      handleFreqChange() {
        const subOpt = [...this.options].find((o) => o.dataset.option === "subscription");
        if (!subOpt) return;
        // Changing frequency implies the subscribe option.
        if (!subOpt.classList.contains("is-selected")) {
          this.selectOption(subOpt);
          return;
        }
        const optionEl = this.freqSelect.options[this.freqSelect.selectedIndex];
        if (!optionEl) return;

        if (this.sellingPlanInput) this.sellingPlanInput.value = optionEl.value;
        this.updateOptionDisplay(subOpt, optionEl);
        this.applyPrice(optionEl);
        this.updateURLParamsIfEnabled(optionEl.value);
      }

      // Update the price / compare-at / per-day text shown inside an option card.
      updateOptionDisplay(opt, dataEl) {
        const priceEl = opt.querySelector("[data-price-display]");
        const compareEl = opt.querySelector("[data-compare-display]");
        const perDayEl = opt.querySelector("[data-perday-display]");

        if (priceEl && dataEl.dataset.price) priceEl.textContent = dataEl.dataset.price;

        const compareRaw = Number(dataEl.dataset.compareAtPriceRaw);
        const priceRaw = Number(dataEl.dataset.priceRaw);
        if (compareEl) {
          if (compareRaw > priceRaw && dataEl.dataset.compareAtPrice) {
            compareEl.textContent = dataEl.dataset.compareAtPrice;
            compareEl.style.display = "";
          } else {
            compareEl.style.display = "none";
          }
        }

        if (perDayEl && dataEl.dataset.perDay) perDayEl.textContent = dataEl.dataset.perDay;
      }

      applyPrice(dataEl) {
        const priceData = {
          price: dataEl.dataset.priceFormatted,
          compareAtPrice: dataEl.dataset.compareAtPriceFormatted,
          priceRaw: Number(dataEl.dataset.priceRaw),
          compareAtPriceRaw: Number(dataEl.dataset.compareAtPriceRaw),
        };
        this.updatePriceBlock(priceData);
        this.updateStickyPriceBlock(priceData);
      }

      /* ---- ported verbatim from subscription-widget.js ---- */

      updatePriceBlock({ price, compareAtPrice, priceRaw, compareAtPriceRaw }) {
        const sectionId = this.closest("[data-section-id]")?.dataset?.sectionId;
        if (!sectionId) return;

        const priceBlock = document.getElementById(`price-${sectionId}`);
        if (!priceBlock) return;

        const priceContainer = priceBlock.querySelector(".price");
        if (!priceContainer) return;

        const isOnSale = compareAtPriceRaw > priceRaw;

        priceContainer.classList.toggle("price--on-sale", isOnSale);

        const regularPriceEl = priceContainer.querySelector(".price__regular .wt-product__price__final");
        if (regularPriceEl) regularPriceEl.innerHTML = price;

        const salePriceEl = priceContainer.querySelector(".price__sale .wt-product__price__final");
        if (salePriceEl) salePriceEl.innerHTML = price;

        const saleDiv = priceContainer.querySelector(".price__sale");
        if (saleDiv) {
          saleDiv.classList.toggle("visible", isOnSale);
          saleDiv.classList.remove("visible-main-product");
        }

        const compareEl = priceContainer.querySelector(".wt-product__price__compare");
        if (compareEl) {
          compareEl.innerHTML = isOnSale ? compareAtPrice : "";
          compareEl.classList.toggle("hidden", !isOnSale);
        }

        let percentEl = priceContainer.querySelector(".price-item--percent");
        if (isOnSale) {
          const percent = Math.round(((compareAtPriceRaw - priceRaw) / compareAtPriceRaw) * 100);
          if (percentEl) {
            percentEl.textContent = `-${percent}%`;
          } else {
            const wrapper = priceContainer.querySelector(".price__sale__details-wrapper");
            if (wrapper) {
              percentEl = document.createElement("span");
              percentEl.className = "price-item--percent";
              percentEl.textContent = `-${percent}%`;
              wrapper.appendChild(percentEl);
            }
          }
        } else if (percentEl) {
          percentEl.remove();
        }
      }

      updateStickyPriceBlock({ price, compareAtPrice, priceRaw, compareAtPriceRaw }) {
        const sectionId = this.closest("[data-section-id]")?.dataset?.sectionId;
        if (!sectionId) return;

        const stickyPriceBlock = document.getElementById(`sticky-price-${sectionId}`);
        if (!stickyPriceBlock) return;

        const priceContainer = stickyPriceBlock.querySelector(".price");
        if (!priceContainer) return;

        const isOnSale = compareAtPriceRaw > priceRaw;

        priceContainer.classList.toggle("price--on-sale", isOnSale);

        const regularPriceEl = priceContainer.querySelector(".price__regular .wt-product__price__final");
        if (regularPriceEl) regularPriceEl.innerHTML = price;

        const salePriceEl = priceContainer.querySelector(".price__sale .wt-product__price__final");
        if (salePriceEl) salePriceEl.innerHTML = price;

        const saleDiv = priceContainer.querySelector(".price__sale");
        if (saleDiv) {
          saleDiv.classList.toggle("visible", isOnSale);
          saleDiv.classList.remove("visible-main-product");
        }

        const compareEl = priceContainer.querySelector(".wt-product__price__compare");
        if (compareEl) {
          compareEl.innerHTML = isOnSale ? compareAtPrice : "";
          compareEl.classList.toggle("hidden", !isOnSale);
        }

        let percentEl = priceContainer.querySelector(".price-item--percent");
        if (isOnSale) {
          const percent = Math.round(((compareAtPriceRaw - priceRaw) / compareAtPriceRaw) * 100);
          if (percentEl) {
            percentEl.textContent = `-${percent}%`;
          } else {
            const wrapper = priceContainer.querySelector(".price__sale__details-wrapper");
            if (wrapper) {
              percentEl = document.createElement("span");
              percentEl.className = "price-item--percent";
              percentEl.textContent = `-${percent}%`;
              wrapper.appendChild(percentEl);
            }
          }
        } else if (percentEl) {
          percentEl.remove();
        }
      }

      updateURLParamsIfEnabled(planId) {
        const wrapper = this.closest("[data-update-url-on-plan-change]");
        if (wrapper?.dataset.updateUrlOnPlanChange === "false") return;

        const urlParams = new URLSearchParams(window.location.search);
        if (planId) {
          urlParams.set("selling_plan", planId);
        } else {
          urlParams.delete("selling_plan");
        }
        window.history.pushState({}, "", `${window.location.pathname}?${urlParams.toString()}`);
      }

      /* ---- variant change: swap in freshly rendered markup, then re-init ---- */

      subscribeVariantChange() {
        if (typeof subscribe !== "function" || typeof PUB_SUB_EVENTS === "undefined") return;

        this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, (e) => {
          const wrapper = this.closest("[data-section-id]");
          const sectionId = wrapper?.dataset?.sectionId;
          const originalSectionId = wrapper?.dataset?.originalSectionId;
          if (sectionId && e.data.sectionId !== sectionId && e.data.sectionId !== originalSectionId) return;

          const parsedHTML = e.data.html.querySelector(".pludo-sub__container");
          if (!parsedHTML || !this.container) return;

          this.container.innerHTML = parsedHTML.innerHTML;
          this.init();

          if (originalSectionId) {
            const hiddenInput = this.querySelector('input[name="selling_plan"]');
            if (hiddenInput) hiddenInput.setAttribute("form", `product-form-${sectionId}`);
          }
        });
      }
    }
  );
}
